import React, { forwardRef, useEffect, useState } from 'react';

import {
  Anchor,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  Space,
  Text,
  TextInput,
  Title,
  UnstyledButton,
  createStyles
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import bgImage from 'assets/images/bg.png';
import { AxiosError, HttpStatusCode } from 'axios';
import { AuthConnectionHeader } from 'components/AuthConnectionHeader';
import { Branding } from 'components/Branding';
import { GoogleAuthButton } from 'components/GoogleAuthButton';
import { LanguageChange } from 'components/LanguageChange';
import { PasswordInputWithStrength } from 'components/PasswordInputWithValidation';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from 'services/auth-service';
import { NotificationService } from 'services/notification-service';
import { ShopifyService } from 'services/shopify-service';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import {
  MAX_PASSWORD_STRENGTH,
  confirmPasswordValidator,
  emailValidator,
  maxLengthValidator,
  minLengthValidator,
  passwordStrengthValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';
import { extractRedirectPathFromQueryParams, navigateToLandingPage } from 'shared/utils/navigation';

interface SelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  value: string;
  disabled?: boolean;
}

const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(({ label, ...others }: SelectItemProps, ref) => (
  <Box ref={ref} {...others}>
    {label === '' ? (
      <Group position="center">
        <Loader variant="dots" />
      </Group>
    ) : (
      <Text size="sm">{label}</Text>
    )}
  </Box>
));

SelectItem.displayName = 'SelectItem';

const useStyles = createStyles(() => ({
  languageChangeBox: {
    position: 'absolute',
    left: 20,
    bottom: 20,
    [`@media (max-height: 880px)`]: {
      position: 'relative',
      left: 0,
      bottom: 0,
      marginTop: 20,
      marginBottom: 20
    }
  }
}));

export function SignUpPage() {
  const [successModalOpened, setSuccessModalOpened] = useState(false);
  const [triggerGoogleAuth, setTriggerGoogleAuth] = useState(false);

  const location = useLocation();
  const { google } = queryString.parse(location.search);
  const { classes } = useStyles();

  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof google === 'string' && google.trim() !== '' && google === 'true') setTriggerGoogleAuth(true);
  }, []);

  const signUpForm = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    validate: {
      firstName: (value) => requiredInputValidator(value) || minLengthValidator(value) || maxLengthValidator(value),
      lastName: (value) => requiredInputValidator(value) || minLengthValidator(value) || maxLengthValidator(value),
      email: (value) => requiredInputValidator(value) || emailValidator(value),
      password: (value) => requiredInputValidator(value) || passwordStrengthValidator(value, MAX_PASSWORD_STRENGTH),
      confirmPassword: (value, values) =>
        requiredInputValidator(value) || confirmPasswordValidator(values.password, value)
    }
  });

  const registerSuperAdminMutation = useMutation({
    mutationFn: AuthService.registerSuperAdmin,
    onSuccess: () => setSuccessModalOpened(true),
    onError: (error: AxiosError) => {
      const errorCode = error.response?.status;
      if (errorCode === HttpStatusCode.Conflict) {
        NotificationService.showResult(false, t('AUTH.ERROR.CONFLICT'));
      } else if (errorCode === HttpStatusCode.UnprocessableEntity) {
        NotificationService.showResult(false, t('AUTH.ERROR.CONFLICT_EMAIL_USED_BY_PROVIDER'));
      } else {
        NotificationService.showResult(false, t('AUTH.ERROR.SIGN_UP'));
      }
    }
  });

  const signUp = (referral?: string) => {
    if (signUpForm.validate().hasErrors) return;

    const redirect = extractRedirectPathFromQueryParams(queryString.parse(location.search));

    registerSuperAdminMutation.mutate({
      ...signUpForm.values,
      referral,
      redirect: redirect ? encodeURIComponent(redirect) : undefined
    });
  };

  const navigateToLogin = () => {
    navigate(ROUTE_LINKS.login + (location.search ?? ''));
  };

  return (
    <>
      <Modal opened={successModalOpened} onClose={() => navigateToLogin()} withCloseButton={false}>
        <Flex direction="column" align="center" w="90%" ml="auto" mr="auto">
          <Branding width={120} />
          <Space h={10} />
          <Title order={3} align="center">
            {t('AUTH.SUCCESS_SIGN_UP_MESSAGE')}
          </Title>

          <Space h={10} />

          <Text w="70%" align="center">
            {t('AUTH.VERIFY_SIGN_UP_MESSAGE')}
          </Text>
          <Space h={20} />
          <Button fullWidth onClick={() => navigateToLogin()}>
            {t('APP.ACTIONS.OK')}
          </Button>
        </Flex>
      </Modal>
      <Flex
        direction="column"
        justify="center"
        align="center"
        mih="100svh"
        w="100vw"
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',

          backgroundPosition: 'center'
        }}>
        <Flex direction="row" align="center" justify="center">
          <Paper
            withBorder
            p={30}
            sx={{
              '@keyframes slideIn': {
                '0%': {
                  transform: 'translateX(-50%)'
                },
                '100%': {
                  transform: 'translateX(5%)'
                }
              }
            }}>
            <Center mb={10}>
              {
                // User is connecting their Shopify Store
                ShopifyService.isShopifyStoreDataInLocalStorage() ? (
                  <AuthConnectionHeader
                    connectionTitle={ShopifyService.getShopifyShopUrlFromLocalStorage() ?? ''}
                    connectionAppIcon={<ShopifyIcon w={30} h={30} />}
                    type="signUp"
                  />
                ) : (
                  <UnstyledButton onClick={() => navigateToLandingPage()}>
                    <Branding width={110} />
                  </UnstyledButton>
                )
              }
            </Center>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                const form = event.target as HTMLFormElement;
                const referralValue = (form?.elements.namedItem('referral') as HTMLInputElement)?.value;
                signUp(referralValue);
              }}
              data-rewardful>
              <TextInput
                label={t('AUTH.FIRST_NAME.LABEL')}
                placeholder={t('AUTH.FIRST_NAME.PLACEHOLDER')}
                {...signUpForm.getInputProps('firstName')}
              />
              <TextInput
                label={t('AUTH.LAST_NAME.LABEL')}
                mt="md"
                placeholder={t('AUTH.LAST_NAME.PLACEHOLDER')}
                {...signUpForm.getInputProps('lastName')}
              />
              <TextInput
                label={t('AUTH.EMAIL.LABEL')}
                mt="md"
                placeholder={t('AUTH.EMAIL.PLACEHOLDER')}
                {...signUpForm.getInputProps('email')}
              />
              <Space h={15} />
              <PasswordInputWithStrength
                newPassword={false}
                passwordInputProps={signUpForm.getInputProps('password')}
                confirmPasswordInputProps={signUpForm.getInputProps('confirmPassword')}
              />
              <Space h={20} />
              <Button type="submit" fullWidth loading={registerSuperAdminMutation.isLoading}>
                {t('AUTH.SIGN_UP')}
              </Button>
              <Space h={10} />
              <GoogleAuthButton
                disabled={registerSuperAdminMutation.isLoading}
                trigger={triggerGoogleAuth}
                fromRegister
              />
              <Space h={20} />
              <Text w={260} color="dimmed" align="center" ml="auto" mr="auto">
                {t('AUTH.TERMS.START')}
                <Anchor href="https://www.droxy.ai/terms" target="_blank">
                  {t('AUTH.TERMS.ANCHOR_1')}
                </Anchor>
                {t('AUTH.TERMS.AND')}
                <Anchor href="https://www.droxy.ai/privacy" target="_blank">
                  {t('AUTH.TERMS.ANCHOR_2')}
                </Anchor>
              </Text>
              <Flex direction="row" mt={10}>
                <Text>{t('AUTH.ALREADY_HAVE_ACCOUNT')}</Text>
                <Space w={5} />
                <Text
                  sx={() => ({
                    '&:hover': {
                      cursor: 'pointer'
                    },
                    textDecoration: 'underline'
                  })}
                  onClick={() => navigateToLogin()}>
                  {t('AUTH.SIGN_IN')}
                </Text>
              </Flex>
            </form>
          </Paper>
        </Flex>
        <Box className={classes.languageChangeBox}>
          <LanguageChange />
        </Box>
      </Flex>
    </>
  );
}
